import React, { useState, useCallback } from "react";
import PageGuide from "../../components/PageGuide";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Container, Stack, Typography, Breadcrumbs, Box, Paper,
  TableHead, TableBody, TableRow, TableCell, TableContainer,
  TablePagination, Button, Chip, CircularProgress, Alert,
  FormControl, InputLabel, Select, MenuItem, Collapse,
} from "@mui/material";
import {
  HomeRounded, ArrowBackRounded, DownloadRounded,
  LocalShippingRounded, ExpandMoreRounded, ExpandLessRounded,
} from "@mui/icons-material";
import ApiCall from "../../utils/apicall";

import SmartTable from "../../components/SmartTable/SmartTable";
// ─── helpers ─────────────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString();
}

function exportCSV(bols) {
  const header = [
    "BOL Title", "Delivery Date", "Order Date", "Ship Via",
    "Terms", "Ship-To Address", "Company", "Piece Line Count",
    "Total Quantity", "Piece Marks", "PO IDs",
  ];
  const rows = bols.map((b) => [
    b.bol_title || "",
    b.delivery_date ? new Date(b.delivery_date).toLocaleDateString() : "",
    b.order_date   ? new Date(b.order_date).toLocaleDateString()    : "",
    b.ship_via     || "",
    b.terms        || "",
    b.ship_to_address || "",
    b.company_name || "",
    b.piece_line_count ?? "",
    b.total_quantity   ?? "",
    (b.piece_marks || []).join(", "),
    (b.po_ids      || []).join(", "),
  ]);
  const csv = [header, ...rows]
    .map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url  = URL.createObjectURL(blob);
  const a    = Object.assign(document.createElement("a"), {
    href: url, download: "shipping-load-summary.csv",
  });
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// ─── component ───────────────────────────────────────────────────────────────

export default function ShippingLoadSummaryReport() {
  const navigate = useNavigate();

  const [jobs,        setJobs]        = useState([]);
  const [jobId,       setJobId]       = useState("");
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [error,       setError]       = useState(null);
  const [page,        setPage]        = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expanded,    setExpanded]    = useState({});

  // Load job list on first render
  React.useEffect(() => {
    setJobsLoading(true);
    ApiCall.get("/jobs")
      .then((r) => setJobs(r.data?.data || r.data || []))
      .catch(() => {})
      .finally(() => setJobsLoading(false));
  }, []);

  const fetchReport = useCallback((jid) => {
    if (!jid) return;
    setLoading(true);
    setError(null);
    setData(null);
    setPage(0);
    setExpanded({});
    ApiCall.get(`/reports/shipping-load?jobId=${jid}`)
      .then((r) => setData(r.data?.data))
      .catch(() => setError("Failed to load Shipping Load Summary"))
      .finally(() => setLoading(false));
  }, []);

  const handleJobChange = (e) => {
    const val = e.target.value;
    setJobId(val);
    fetchReport(val);
  };

  const toggleExpand = (id) =>
    setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const bols = data?.bols || [];
  const paginated = bols.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <Helmet><title>Shipping Load Summary | SFM</title></Helmet>
      <Container maxWidth="xl" sx={{ py: 3 }}>

        {/* ── header ── */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Breadcrumbs>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <HomeRounded fontSize="small" />
              <Typography variant="body2" color="text.secondary">Reports</Typography>
            </Stack>
            <Typography variant="body2" fontWeight={600}>Shipping Load Summary</Typography>
          </Breadcrumbs>
          <Stack direction="row" spacing={1}>
            {data && bols.length > 0 && (
              <Button variant="outlined" size="small" startIcon={<DownloadRounded />}
                onClick={() => exportCSV(bols)}>
                Export CSV
              </Button>
            )}
            <Button variant="text" size="small" startIcon={<ArrowBackRounded />}
              onClick={() => navigate("/reports")}>
              All Reports
            </Button>
          </Stack>
        </Stack>
        <PageGuide
          why="Summarize what shipped on each load — so deliveries are documented."
          who="Shipping and the QM, documenting each load."
          what="A summary of shipping loads and the pieces on each."
          how="Filter by job or load, then export."
          when="For delivery records and customer reporting."
          standard="AISC 303 §4"
        />

        {/* ── job selector ── */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <LocalShippingRounded color="action" />
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
              Select a Job
            </Typography>
            <FormControl size="small" sx={{ minWidth: 320 }}>
              <InputLabel>Job</InputLabel>
              <Select value={jobId} label="Job" onChange={handleJobChange}>
                {jobsLoading && <MenuItem disabled>Loading jobs…</MenuItem>}
                {jobs.map((j) => (
                  <MenuItem key={j.id} value={j.id}>
                    {j.jobNumber ? `${j.jobNumber} — ` : ""}{j.projectName || j.name || `Job ${j.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Paper>

        {/* ── summary tiles ── */}
        {data && (
          <Stack direction="row" spacing={2} mb={3} flexWrap="wrap">
            <Paper variant="outlined" sx={{ px: 3, py: 1.5, textAlign: "center" }}>
              <Typography variant="h5" fontWeight={700}>{data.totalBols}</Typography>
              <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight={600}>
                Loads / BOLs
              </Typography>
            </Paper>
            <Paper variant="outlined" sx={{ px: 3, py: 1.5, textAlign: "center" }}>
              <Typography variant="h5" fontWeight={700}>{data.totalPieces}</Typography>
              <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight={600}>
                Total Pieces
              </Typography>
            </Paper>
          </Stack>
        )}

        {/* ── states ── */}
        {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
        {loading && (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading…</Typography>
          </Box>
        )}
        {!loading && data && bols.length === 0 && (
          <Alert severity="info">No shipments found for this job.</Alert>
        )}
        {!jobId && !loading && !data && (
          <Alert severity="info">Select a job above to view its shipping loads.</Alert>
        )}

        {/* ── table ── */}
        {!loading && bols.length > 0 && (
          <>
            <TableContainer component={Paper} variant="outlined">
              <SmartTable tableId="report-shipping-loads" lockedColumns={["BOL Title"]} size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.50" }}>
                    <TableCell width={32} />
                    <TableCell><strong>BOL Title</strong></TableCell>
                    <TableCell><strong>Company</strong></TableCell>
                    <TableCell><strong>Delivery Date</strong></TableCell>
                    <TableCell><strong>Ship Via</strong></TableCell>
                    <TableCell align="right"><strong>Pieces</strong></TableCell>
                    <TableCell align="right"><strong>Qty</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.map((b) => (
                    <React.Fragment key={b.bol_id}>
                      <TableRow hover
                        sx={{ "& td": { borderBottom: expanded[b.bol_id] ? "none" : undefined } }}>
                        <TableCell>
                          <Button
                            size="small"
                            onClick={() => toggleExpand(b.bol_id)}
                            sx={{ minWidth: 0, p: 0.5 }}
                          >
                            {expanded[b.bol_id]
                              ? <ExpandLessRounded fontSize="small" />
                              : <ExpandMoreRounded fontSize="small" />}
                          </Button>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {b.bol_title || `BOL #${b.bol_id}`}
                        </TableCell>
                        <TableCell>{b.company_name || "—"}</TableCell>
                        <TableCell>{fmtDate(b.delivery_date)}</TableCell>
                        <TableCell>
                          {b.ship_via
                            ? <Chip label={b.ship_via} size="small" variant="outlined" />
                            : "—"}
                        </TableCell>
                        <TableCell align="right">{b.piece_line_count ?? "—"}</TableCell>
                        <TableCell align="right">{b.total_quantity ?? "—"}</TableCell>
                      </TableRow>

                      {/* ── expand row ── */}
                      <TableRow>
                        <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                          <Collapse in={!!expanded[b.bol_id]} timeout="auto" unmountOnExit>
                            <Box sx={{
                              px: 4, py: 2,
                              bgcolor: "grey.50",
                              borderBottom: "1px solid #e0e0e0",
                            }}>
                              <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                                <Box>
                                  <Typography variant="caption" color="text.secondary"
                                    fontWeight={700} textTransform="uppercase">
                                    Order Date
                                  </Typography>
                                  <Typography variant="body2" mt={0.5}>
                                    {fmtDate(b.order_date)}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary"
                                    fontWeight={700} textTransform="uppercase">
                                    Terms
                                  </Typography>
                                  <Typography variant="body2" mt={0.5}>
                                    {b.terms || "—"}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary"
                                    fontWeight={700} textTransform="uppercase">
                                    Ship-To Address
                                  </Typography>
                                  <Typography variant="body2" mt={0.5}>
                                    {b.ship_to_address || "—"}
                                  </Typography>
                                </Box>
                                {b.po_ids?.length > 0 && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary"
                                      fontWeight={700} textTransform="uppercase">
                                      Purchase Orders
                                    </Typography>
                                    <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap">
                                      {b.po_ids.map((p) => (
                                        <Chip key={p} label={`PO ${p}`} size="small" />
                                      ))}
                                    </Stack>
                                  </Box>
                                )}
                              </Stack>

                              {b.piece_marks?.length > 0 && (
                                <Box mt={2}>
                                  <Typography variant="caption" color="text.secondary"
                                    fontWeight={700} textTransform="uppercase">
                                    Piece Marks ({b.piece_marks.length})
                                  </Typography>
                                  <Box mt={0.5} sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                    {b.piece_marks.map((pm) => (
                                      <Chip key={pm} label={pm} size="small"
                                        variant="outlined" color="primary" />
                                    ))}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </SmartTable>
            </TableContainer>
            <TablePagination
              component="div"
              count={bols.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </>
        )}
      </Container>
    </>
  );
}
